import axios from "axios";
import React, { useEffect, useState } from "react";
import { api } from "../utils/Api";
import { Typography, Flex, Table, Statistic, notification } from "antd";
import Layout from "../layout/Layout";
import { CustomSearch, CustomSpinner } from "../utils/CustomComponents";
import dayjs from "dayjs";

const { Text, Title } = Typography;

const WeeklyScheduleReport = () => {
	const [loading, setLoading] = useState(false);
	const [startDate, setStartDate] = useState(dayjs());
	const [endDate, setEndDate] = useState(dayjs());
	const [list, setList] = useState([]);
	const [apiNotification, contextHolder] = notification.useNotification();
	const [columns, setColumns] = useState([]);
	const [totalYards, setTotalYards] = useState(0);

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	useEffect(() => {
		getJobs();
	}, [startDate, endDate]);

	const getDatesBetween = (startDate, endDate) => {
		const dates = [];
		let current = startDate.startOf("day");

		while (current.isBefore(endDate) || current.isSame(endDate, "day")) {
			dates.push(current.format("MM/DD/YYYY"));
			current = current.add(1, "day");
		}

		return dates;
	};

	const getJobs = async () => {
		setLoading(true);

		const start = `${startDate.year()}-${
			startDate.month() + 1
		}-${startDate.date()}`;
		const end = `${endDate.year()}-${endDate.month() + 1}-${endDate.date()}`;

		const url =
			api + `/job/get/yards/byplant?startDate=${start}&endDate=${end}`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		const dates = getDatesBetween(startDate, endDate);

		setColumns([
			{
				title: "Plant",
				dataIndex: "plant",
				key: "plant",
				render: (value) => {
					return <Text>{value}</Text>;
				},
			},
			...dates.map((date) => ({
				title: date,
				dataIndex: date,
				key: date,
				render: (value) => {
					return (
						<Statistic
							title=""
							value={value}
							precision={2}
							valueStyle={{ fontSize: 14 }}
						/>
					);
				},
			})),
		]);

		try {
			const { data } = await axios.get(url);
			if (data.success) {
				const groupedByPlant = data.payload.reduce((acc, item) => {
					const plant = item.plant || "Unknown Plant";
					if (!acc[plant]) {
						acc[plant] = {};
					}

					acc[plant][item.date] = (acc[plant][item.date] || 0) + item.totalYD3;
					return acc;
				}, {});

				const transformedData = Object.entries(groupedByPlant).map(
					([plant, dates]) => {
						return { plant, ...dates };
					},
				);
				setList(transformedData);

				const total = data.payload.reduce(
					(sum, item) => sum + (item.totalYD3 || 0),
					0,
				);
				setTotalYards(total);
			} else {
				openNotification("error", "Error", data.message);
			}
			setLoading(false);
		} catch (err) {
			openNotification("error", "Error", "Error while trying to get jobs");
			console.error(err.message);
			return null;
		}
	};

	const RenderTable = () => {
		return (
			<Flex gap={"middle"} vertical>
				<Title level={4}>Total Target YD: {totalYards}</Title>
				<Table
					columns={columns}
					dataSource={list}
					pagination={{
						position: ["bottomRight"],
						pageSizeOptions: [50, 100],
						defaultPageSize: 50,
						showSizeChanger: true,
					}}
					scroll={{
						x: "max-content",
					}}
					style={{
						minWidth: "100%",
					}}
				/>
			</Flex>
		);
	};

	const onRangeChange = (dates) => {
		if (dates) {
			setStartDate(dates[0]);
			setEndDate(dates[1]);
		}
	};

	const RenderDates = () => {
		return (
			<CustomSearch
				loading={loading}
				startDate={startDate}
				endDate={endDate}
				dateFormat={"MM/DD/YYYY"}
				onRangeChange={onRangeChange}
			/>
		);
	};

	return (
		<Layout
			title="Weekly Schedule [CY] Report "
			description="Job Report"
			type="large"
		>
			<Flex vertical gap={"large"}>
				<RenderDates />
				{loading ? <CustomSpinner /> : <RenderTable />}
			</Flex>
			{contextHolder}
		</Layout>
	);
};

export default WeeklyScheduleReport;
